import {Box, Button, Divider, Drawer, FormControl, InputLabel, MenuItem, Select, TextField} from '@mui/material'
import dayjs from 'dayjs'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import fileDownload from 'js-file-download'
import {useState} from 'react'
import {useDropzone} from 'react-dropzone'
import {serverList} from 'utils/const/globalConst'
import {resultAlert} from 'utils/CustomAlert/Alert'
import {envBackHost} from '../../../GLOBAL/envVariables'

const DeviceFotaBatchOffcanvas = (props: any) => {
  const fotaInit = {
    lwm2mIp: '',
    lwm2mPort: '',
    imageIpPort: '',
    firmwareVersion: '',
  }

  const {toast, open, onClose} = props
  const [file, setFile] = useState<File | undefined>(undefined)
  const [fileName, setFileName] = useState<string>('')
  const [processing, setProcessing] = useState<boolean>(false)
  const [fotaData, setFotaData] = useState(fotaInit)

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        setFile(file)
        setFileName(file.name)
      }
    },
  })

  const reset = () => {
    setFotaData(fotaInit)
    setFile(undefined)
    setFileName('')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const exampleExcelDownload = async () => {
    const fileName = `device_assign_batch_example_${dayjs().format('YYYY-MM-DD HH:mm')}.xlsx`
    axiosWithAuth()
      .get(`${envBackHost}/device/excel`, {
        responseType: 'blob',
      })
      .then(async (res) => {
        console.log(res)
        await resultAlert({resultCode: res.status})
        if (res.status === 200) {
          fileDownload(res.data, fileName)
        }
      })
  }

  const fileUploadHandler = async () => {
    const formData = new FormData()
    formData.append('file', file as Blob)
    formData.append('lwm2mIp', fotaData.lwm2mIp)
    formData.append('lwm2mPort', fotaData.lwm2mPort)
    formData.append('imageIpPort', fotaData.imageIpPort)
    formData.append('firmwareVersion', fotaData.firmwareVersion)

    setProcessing(true)
    await toast.promise(
      axiosWithAuth()
        .post(`${envBackHost}/device/fota/excel`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(async (res) => {
          setProcessing(false)
          await resultAlert(res.data)
          if (res.data.resultCode === 0) {
            reset()
            handleClose()
            setProcessing(false)
            return
          } else if (res.data.resultCode === 30) {
            setProcessing(false)
            return
          }
        })
        .catch(async (e) => {
          await resultAlert({resultCode: e.response.status})
          setProcessing(false)
        })
        .finally(() => {
          setProcessing(false)
        }),
      {
        pending: '단말기 서버 일괄 변경 중 입니다.',
      },
      {
        position: 'top-center',
        closeButton: false,
        className: 'fs-3  fw-bold',
        style: {width: '400px'},
      }
    )
  }

  const selectedServerId = serverList.find((s) => s.lwm2mIp === fotaData?.lwm2mIp)?.id ?? ''

  const selectServerHandler = (serverId: any) => {
    const selected = serverList.find((server) => server.id === serverId)
    if (!selected) return
    setFotaData({
      lwm2mIp: selected.lwm2mIp,
      lwm2mPort: selected.lwm2mPort,
      imageIpPort: selected.imageIpPort,
      firmwareVersion: selected.firmwareVersion,
    })
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={handleClose}
      PaperProps={{sx: {width: '30%', display: 'flex', flexDirection: 'column'}}}
    >
      {/* 헤더 */}
      <Box sx={{px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
        <span className='font-semibold text-[1.5rem]'>단말기 서버 일괄 변경</span>
      </Box>

      {/* 본문 */}
      <Box sx={{flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3}}>
        {/* 파일 업로드 + 샘플 다운로드 */}
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
          <Box sx={{flex: 1}}>
            <div
              {...getRootProps()}
              className={`w-full h-20 border-2 border-dashed rounded-lg flex items-center px-3 cursor-pointer overflow-hidden transition-colors
                ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}
            >
              <input {...getInputProps()} />
              <span className='truncate text-blue-600 font-medium'>
                {fileName ? fileName : '파일을 선택해주세요.'}
              </span>
            </div>
          </Box>
          <Button
            variant='contained'
            color='success'
            sx={{minWidth: 'auto', width: '160px', height: '60px', flexShrink: 0}}
            onClick={exampleExcelDownload}
          >
            샘플양식 다운로드
          </Button>
        </Box>

        <Divider sx={{fontWeight: 600, fontSize: '1.5rem'}}>서버 정보 입력</Divider>

        {/* 서버 선택 + 필드 */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          <FormControl sx={{width: 300}} size='small'>
            <InputLabel sx={{fontSize: 14}} id='fota-server-label'>
              단말기 통신 서버
            </InputLabel>
            <Select
              labelId='fota-server-label'
              value={selectedServerId || ''}
              onChange={(e) => selectServerHandler(e.target.value)}
              label='단말기 통신 서버'
              sx={{fontSize: 14}}
            >
              {serverList.map((server) => (
                <MenuItem key={server.id} value={server.id}>
                  {server.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {[
            {label: '서버 주소', value: fotaData.lwm2mIp},
            {label: '서버 포트', value: fotaData.lwm2mPort},
            {label: '이미지 서버 주소', value: fotaData.imageIpPort},
            {label: '펌웨어 버전', value: fotaData.firmwareVersion},
          ].map(({label, value}) => (
            <Box key={label} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '70%'}}>
              <span className='font-semibold text-[1.25rem]'>{label}</span>
              <TextField
                variant='outlined'
                className='mx-2 bg-gray-100'
                value={value}
                inputProps={{readOnly: true}}
                size='small'
                autoComplete='off'
              />
            </Box>
          ))}
        </Box>

        <Divider sx={{fontWeight: 600, fontSize: '1.5rem'}}>매뉴얼</Divider>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}} className='text-slate-700 font-semibold text-[1.1rem]'>
          <span>
            1. 해당 단말기의 최초 펌웨어가{' '}
            <span className='text-blue-500'>'TM800L_101_800'</span>인 경우 해당 변경 기능을
            사용할 수 있습니다.
          </span>
          <span>2. 단말기 일괄 등록에 사용한 엑셀 양식과 동일하게 사용 가능합니다.</span>
          <span>3. 변경하고자 하는 서버에도 단말 일괄 등록이 필요합니다.</span>
          <span>4. 서버 변경은 일괄변경 이후 최초 통신 시 서버 변경 작업이 진행됩니다.</span>
          <span>5. 변경 확인은 최초 통신 이후의 이미지 확인 시 목적지 서버에서 확인 가능합니다.</span>
        </Box>
      </Box>

      {/* 푸터 */}
      <Box sx={{px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2}}>
        <Button
          variant='contained'
          size='large'
          color='info'
          disabled={processing || !file || !selectedServerId}
          onClick={fileUploadHandler}
        >
          일괄변경
        </Button>
        <Button variant='contained' size='large' color='error' onClick={handleClose}>
          나가기
        </Button>
      </Box>
    </Drawer>
  )
}
export default DeviceFotaBatchOffcanvas
